/**
 * FFP AES-GCM encryption and decryption core (TypeScript / Web Crypto API).
 *
 * Matches the behavior of the original FFPEncryptor.cs:
 *  - AES-GCM with a 128-bit authentication tag appended to the ciphertext (default Web Crypto behavior)
 *  - the original _tag is treated as AAD (additionalData), and this implementation preserves that semantics
 *  - ciphertext is represented as uppercase hexadecimal text for input/output
 *
 * Relies only on window.crypto (browser) / globalThis.crypto (Node 18+) - no Angular dependency,
 * so this file can also be reused by the standalone verification tool in tools/ffp-encryptor-verify.
 */

function getSubtle(): SubtleCrypto {
  const c: Crypto | undefined =
    (typeof globalThis !== 'undefined' && (globalThis as any).crypto) || undefined;
  if (!c || !c.subtle) {
    throw new Error('Web Crypto (crypto.subtle) is unavailable. Run this in HTTPS/localhost or in a Node 18+ environment.');
  }
  return c.subtle;
}

export function hexToBytes(hex: string): Uint8Array {
  if (hex.length % 2 !== 0) {
    throw new Error('HEX string length must be even.');
  }
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
  }
  return bytes;
}

export function bytesToHex(bytes: Uint8Array): string {
  let hex = '';
  for (let i = 0; i < bytes.length; i++) {
    hex += bytes[i].toString(16).padStart(2, '0');
  }
  return hex.toUpperCase();
}

export interface FFPKeyMaterial {
  keyHex: string;
  ivHex: string;
  aadHex: string;
}

export class FFPEncryptor {
  private readonly keyBytes: Uint8Array;
  private readonly ivBytes: Uint8Array;
  private readonly aadBytes: Uint8Array;
  private cryptoKey: CryptoKey | null = null;

  constructor(material: FFPKeyMaterial) {
    this.keyBytes = hexToBytes(material.keyHex);
    this.ivBytes = hexToBytes(material.ivHex);
    this.aadBytes = hexToBytes(material.aadHex);
  }

  private async getKey(): Promise<CryptoKey> {
    if (this.cryptoKey) {
      return this.cryptoKey;
    }
    this.cryptoKey = await getSubtle().importKey(
      'raw',
      this.keyBytes,
      { name: 'AES-GCM' },
      false,
      ['encrypt', 'decrypt']
    );
    return this.cryptoKey;
  }

  async encrypt(plaintext: string): Promise<string> {
    const key = await this.getKey();
    const plainBytes = new TextEncoder().encode(plaintext);
    const buffer = await getSubtle().encrypt(
      {
        name: 'AES-GCM',
        iv: this.ivBytes,
        additionalData: this.aadBytes,
        tagLength: 128,
      },
      key,
      plainBytes
    );
    // Web Crypto returns a buffer containing ciphertext + tag, consistent with the C# implementation.
    return bytesToHex(new Uint8Array(buffer));
  }

  async decrypt(encryptedHex: string): Promise<string> {
    const key = await this.getKey();
    const combined = hexToBytes(encryptedHex);
    const buffer = await getSubtle().decrypt(
      {
        name: 'AES-GCM',
        iv: this.ivBytes,
        additionalData: this.aadBytes,
        tagLength: 128,
      },
      key,
      combined
    );
    return new TextDecoder().decode(buffer);
  }
}
